import { AlertCircle, CheckCircle } from "lucide-react";
import type React from "react";
import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import {
  type FileValidationResult,
  validateFile,
} from "@/utils/fileValidation";
import DualFileInput from "./DualFileInput";

interface FileState {
  file: File | null;
  error: string | null;
  isValidating?: boolean;
}

interface DualFileInputContainerProps {
  onFrontFileChange?: (file: File | null, error: string | null) => void;
  onBackFileChange?: (file: File | null, error: string | null) => void;
  onValidationComplete?: (
    frontValid: boolean,
    backValid: boolean,
    frontFile: File | null,
    backFile: File | null,
  ) => void;
  accept?: string;
  frontDescription?: string;
  backDescription?: string;
  className?: string;
}

// Simple Alert component for error display
const Alert: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      "relative w-full rounded-lg border px-4 py-3 text-sm",
      className,
    )}
  >
    {children}
  </div>
);

const AlertDescription: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="[&_p]:leading-relaxed">{children}</div>;

const DualFileInputContainer: React.FC<DualFileInputContainerProps> = ({
  onFrontFileChange,
  onBackFileChange,
  onValidationComplete,
  accept = "image/*,.pdf",
  frontDescription = "Upload a clear image of the front side",
  backDescription = "Upload a clear image of the back side",
  className
}) => {
  const [frontFile, setFrontFile] = useState<FileState>({ error: null, file: null });
  const [backFile, setBackFile] = useState<FileState>({ error: null, file: null });

  const handleFileValidation = useCallback(async (file: File): Promise<FileValidationResult> => {
    return await validateFile(file);
  }, []);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, type: 'front' | 'back') => {
    const file = event.target.files?.[0] || null;
    
    console.log(`🎭 [DualFileInput] File change event for ${type}:`, {
      hasFile: !!file,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type
    });
    
    if (file) {
      console.log(`⏳ [DualFileInput] Starting validation for ${type} file: ${file.name}`);
      
      // Set validating state
      const validatingState = { file: null, error: null, isValidating: true };
      if (type === 'front') {
        setFrontFile(validatingState);
      } else {
        setBackFile(validatingState);
      }

      try {
        const validationResult = await handleFileValidation(file);
        console.log(`📋 [DualFileInput] Validation complete for ${type}:`, {
          isValid: validationResult.isValid,
          error: validationResult.error,
          originalFile: file.name,
          resultFile: validationResult.file.name,
          sizeChanged: file.size !== validationResult.file.size,
          originalSize: file.size,
          resultSize: validationResult.file.size
        });
        
        if (validationResult.isValid) {
          // File is valid - use the potentially resized file from validation result
          const newFileState = { file: validationResult.file, error: null, isValidating: false };
          
          if (type === 'front') {
            setFrontFile(newFileState);
            onFrontFileChange?.(validationResult.file, null);
          } else {
            setBackFile(newFileState);
            onBackFileChange?.(validationResult.file, null);
          }
        } else {
          // File is invalid
          const newFileState = { file: null, error: validationResult.error, isValidating: false };
          
          if (type === 'front') {
            setFrontFile(newFileState);
            onFrontFileChange?.(null, validationResult.error);
          } else {
            setBackFile(newFileState);
            onBackFileChange?.(null, validationResult.error);
          }
        }

        // Trigger validation complete callback with current state
        const frontValid = type === 'front' ? validationResult.isValid : frontFile.file !== null;
        const backValid = type === 'back' ? validationResult.isValid : backFile.file !== null;
        const frontFileResult = type === 'front' ? (validationResult.isValid ? validationResult.file : null) : frontFile.file;
        const backFileResult = type === 'back' ? (validationResult.isValid ? validationResult.file : null) : backFile.file;
        
        onValidationComplete?.(frontValid, backValid, frontFileResult, backFileResult);

      } catch (error) {
        // Handle validation errors
        const errorMessage = error instanceof Error ? error.message : 'Validation failed';
        const newFileState = { file: null, error: errorMessage, isValidating: false };
        
        if (type === 'front') {
          setFrontFile(newFileState);
          onFrontFileChange?.(null, errorMessage);
        } else {
          setBackFile(newFileState);
          onBackFileChange?.(null, errorMessage);
        }

        // Trigger validation complete callback with current state
        const frontValid = type === 'front' ? false : frontFile.file !== null;
        const backValid = type === 'back' ? false : backFile.file !== null;
        
        onValidationComplete?.(frontValid, backValid, frontFile.file, backFile.file);
      }
    } else {
      // No file selected
      const newFileState = { file: null, error: null, isValidating: false };
      
      if (type === 'front') {
        setFrontFile(newFileState);
        onFrontFileChange?.(null, null);
      } else {
        setBackFile(newFileState);
        onBackFileChange?.(null, null);
      }

      // Trigger validation complete callback
      const frontValid = type === 'front' ? false : frontFile.file !== null;
      const backValid = type === 'back' ? false : backFile.file !== null;
      
      onValidationComplete?.(frontValid, backValid, frontFile.file, backFile.file);
    }
  }, [handleFileValidation, onFrontFileChange, onBackFileChange, onValidationComplete, frontFile.file, backFile.file]);

  // Wrapper function to handle file changes and determine front vs back based on input ID
  const handleFileChangeWrapper = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputId = event.target.id;
    const type = inputId === 'front-card' ? 'front' : 'back';
    handleFileChange(event, type);
  }, [handleFileChange]);

  return (
    <div className="space-y-2">
      {/* Error Messages */}
      {frontFile.error && (
        <Alert className="text-red-800 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              <strong>Front Card Error:</strong> {frontFile.error}
            </AlertDescription>
          </div>
        </Alert>
      )}
      
      {backFile.error && (
        <Alert className="text-red-800 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              <strong>Back Card Error:</strong> {backFile.error}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {/* Enhanced DualFileInput - we need to modify this to handle separate onChange handlers */}
      <div className="space-y-2">
        <DualFileInput
          accept={accept}
          backDescription={backDescription}
          className={className}
          frontDescription={frontDescription}
          onChange={handleFileChangeWrapper}
        />
      </div>

      {/* Success indicators */}
      {frontFile.file && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span>Front card: {frontFile.file.name} uploaded successfully</span>
        </div>
      )}
      
      {backFile.file && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span>Back card: {backFile.file.name} uploaded successfully</span>
        </div>
      )}
    </div>
  );
};

export default DualFileInputContainer;
